import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useCart } from "@/contexts/CartContext";

const POLL_INTERVAL = 2500;
const MAX_POLLS = 8;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const { t } = useLang();
  const { clear } = useCart();
  const [state, setState] = useState({ kind: "verifying" });
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setState({ kind: "expired" });
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ kind: "paid", data });
          clear();
          return;
        }
        if (data.status === "expired") {
          setState({ kind: "expired" });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ kind: "pending", data });
          return;
        }
        attempts += 1;
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch {
        setState({ kind: "expired" });
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      className="max-w-2xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center fade-in"
      data-testid="checkout-success-page"
    >
      {state.kind === "verifying" && (
        <>
          <Clock size={42} strokeWidth={1} className="mx-auto text-[var(--eafg-copper)]" />
          <h1 className="font-serif-display text-4xl md:text-5xl mt-8" data-testid="checkout-status-text">
            {t("checkout.verifying")}
          </h1>
        </>
      )}
      {state.kind === "paid" && (
        <>
          <CheckCircle2 size={48} strokeWidth={1} className="mx-auto text-[var(--eafg-copper)]" />
          <h1 className="font-serif-display text-4xl md:text-5xl mt-8" data-testid="checkout-status-text">
            {t("checkout.paid")}
          </h1>
          {state.data && (
            <p className="text-[var(--eafg-muted)] mt-6 tabular-nums">
              {(state.data.amount_total / 100).toFixed(2)} {state.data.currency?.toUpperCase()}
            </p>
          )}
        </>
      )}
      {state.kind === "pending" && (
        <>
          <Clock size={42} strokeWidth={1} className="mx-auto" />
          <h1 className="font-serif-display text-4xl md:text-5xl mt-8" data-testid="checkout-status-text">
            {t("checkout.pending")}
          </h1>
        </>
      )}
      {state.kind === "expired" && (
        <>
          <XCircle size={42} strokeWidth={1} className="mx-auto text-red-700" />
          <h1 className="font-serif-display text-4xl md:text-5xl mt-8" data-testid="checkout-status-text">
            {t("checkout.expired")}
          </h1>
        </>
      )}
      <Link
        to="/tienda"
        className="inline-block mt-10 border border-[var(--eafg-ink)] px-7 py-4 tracking-eyebrow hover:bg-[var(--eafg-ink)] hover:text-white transition-colors"
        data-testid="checkout-back-shop"
      >
        {t("checkout.backShop")}
      </Link>
    </div>
  );
}
